import {Button, Grid, Typography} from '@mui/material';
import React from 'react';
import {useMutation} from '@apollo/client';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  ReportPackGenericFormType,
  reportPackFormType,
  ReportPackGenericFormInnerType,
  reportPackNomineeTransferFormTypeField,
  reportPackManagerAddress1Field,
  reportPackManagerAddress2Field,
  reportPackManagerCityField,
  reportPackManagerPostcodeField,
  reportPackManagerPhoneNumberField,
  reportPackManagerCompanyNameField,
  reportPackManagerTradingNameField,
  reportPackProviderAccountNumberField,
} from './ReportPackFields';
import {upsertNomineeTransferForm} from './reportPackQueries';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  UpsertNomineeTransferForm,
  UpsertNomineeTransferFormVariables,
} from '../../schema-types/UpsertNomineeTransferForm';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';

export const ReportPackNomineeTransfer: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [onUpsertNomineeForm, {loading: onUpsertNomineeFormLoading}] = useMutation<
    UpsertNomineeTransferForm,
    UpsertNomineeTransferFormVariables
  >(upsertNomineeTransferForm);
  return (
    <ReportPackNomineeTransferFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertNomineeForm({
          variables: {
            contactCuroId: values.contactCuroId,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
            nomineeFormTransferType: values.nomineeFormTransferType,
            providerAccountNumber: values.providerAccountNumber,
            managerAddress1: values.managerAddress1,
            managerAddress2: values.managerAddress2,
            managerCity: values.managerCity,
            managerCompanyName: values.managerCompanyName,
            managerPhoneNumber: values.managerPhoneNumber,
            managerPostcode: values.managerPostcode,
            managerTradingName: values.managerTradingName,
          },
        })
          .then(({data}) => {
            if (data?.upsertNomineeTransferForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertNomineeTransferForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackNomineeTransferForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values} = useAutoForm<ReportPackFormFragment>();

  return (
    <Box maxWidth={500}>
      <Typography>Current provider details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerCompanyNameField.id
            )}
            field={reportPackManagerCompanyNameField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerTradingNameField.id
            )}
            field={reportPackManagerTradingNameField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackProviderAccountNumberField.id
            )}
            field={reportPackProviderAccountNumberField}
          />
        </Grid>
      </Grid>
      <Typography>Current provider address:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerAddress1Field.id
            )}
            field={reportPackManagerAddress1Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerAddress2Field.id
            )}
            field={reportPackManagerAddress2Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackManagerCityField.id)}
            field={reportPackManagerCityField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerPostcodeField.id
            )}
            field={reportPackManagerPostcodeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerPhoneNumberField.id
            )}
            field={reportPackManagerPhoneNumberField}
          />
        </Grid>
      </Grid>
      <Typography>Transfer type:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackNomineeTransferFormTypeField.id
            )}
            field={reportPackNomineeTransferFormTypeField}
          />
        </Grid>
      </Grid>
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackNomineeTransferFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackNomineeTransferForm, {}, {debugPosition: 'bottom'});
